import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Fighter, MartialArtExperience } from "../FightSimulatorReducer";

// UUID 생성 헬퍼 함수
const generateUUID = (): string => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

interface FighterFormProps {
  fighter: Fighter;
  label: string;
  color: "red" | "blue";
  onUpdate: (updates: Partial<Fighter>) => void;
  onAddMartialArt: (martialArt: MartialArtExperience) => void;
  onRemoveMartialArt: (id: string) => void;
}

const FighterForm: React.FC<FighterFormProps> = ({
  fighter,
  label,
  color,
  onUpdate,
  onAddMartialArt,
  onRemoveMartialArt,
}) => {
  const { t } = useTranslation();
  const [newMartialArtType, setNewMartialArtType] = useState("");
  const [newMartialArtDuration, setNewMartialArtDuration] = useState(12);

  const colorClasses = {
    red: {
      border: "border-red-500",
      bg: "bg-red-900/30",
      text: "text-red-300",
      button: "bg-red-600 hover:bg-red-700",
    },
    blue: {
      border: "border-blue-500",
      bg: "bg-blue-900/30",
      text: "text-blue-300",
      button: "bg-blue-600 hover:bg-blue-700",
    },
  };

  const classes = colorClasses[color];

  const handleAddMartialArt = () => {
    if (newMartialArtType.trim()) {
      onAddMartialArt({
        id: generateUUID(),
        type: newMartialArtType.trim(),
        durationMonths: newMartialArtDuration,
      });
      setNewMartialArtType("");
      setNewMartialArtDuration(12);
    }
  };

  return (
    <div
      className={`${classes.bg} ${classes.border} border rounded-xl p-4 space-y-4`}
    >
      {/* 헤더 */}
      <h3 className={`text-xl font-bold ${classes.text} text-center`}>
        {label}
      </h3>

      {/* 이름 */}
      <div>
        <label className="block text-sm text-gray-400 mb-1">
          {t("FightSimulator.name")}
        </label>
        <input
          type="text"
          value={fighter.name}
          onChange={(e) => onUpdate({ name: e.target.value })}
          className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-gray-400 focus:outline-none"
          placeholder={t("FightSimulator.namePlaceholder")}
        />
      </div>

      {/* 신체 정보 - 2열 그리드 */}
      <div className="grid grid-cols-2 gap-3">
        {/* 키 */}
        <div>
          <label className="block text-sm text-gray-400 mb-1">
            {t("FightSimulator.height")}
          </label>
          <div className="flex">
            <input
              type="number"
              value={fighter.height}
              onChange={(e) =>
                onUpdate({ height: Math.max(1, parseInt(e.target.value) || 0) })
              }
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-l-lg text-white focus:border-gray-400 focus:outline-none"
            />
            <span className="px-3 py-2 bg-gray-700 border border-l-0 border-gray-600 rounded-r-lg text-gray-400">
              cm
            </span>
          </div>
        </div>

        {/* 몸무게 */}
        <div>
          <label className="block text-sm text-gray-400 mb-1">
            {t("FightSimulator.weight")}
          </label>
          <div className="flex">
            <input
              type="number"
              value={fighter.weight}
              onChange={(e) =>
                onUpdate({ weight: Math.max(1, parseInt(e.target.value) || 0) })
              }
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-l-lg text-white focus:border-gray-400 focus:outline-none"
            />
            <span className="px-3 py-2 bg-gray-700 border border-l-0 border-gray-600 rounded-r-lg text-gray-400">
              kg
            </span>
          </div>
        </div>

        {/* 나이 */}
        <div>
          <label className="block text-sm text-gray-400 mb-1">
            {t("FightSimulator.age")}
          </label>
          <div className="flex">
            <input
              type="number"
              value={fighter.age}
              onChange={(e) =>
                onUpdate({ age: Math.max(1, parseInt(e.target.value) || 0) })
              }
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-l-lg text-white focus:border-gray-400 focus:outline-none"
            />
            <span className="px-3 py-2 bg-gray-700 border border-l-0 border-gray-600 rounded-r-lg text-gray-400">
              {t("FightSimulator.years")}
            </span>
          </div>
        </div>

        {/* 성별 */}
        <div>
          <label className="block text-sm text-gray-400 mb-1">
            {t("FightSimulator.gender")}
          </label>
          <select
            value={fighter.gender}
            onChange={(e) =>
              onUpdate({ gender: e.target.value as "male" | "female" })
            }
            className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-gray-400 focus:outline-none"
          >
            <option value="male">{t("FightSimulator.male")}</option>
            <option value="female">{t("FightSimulator.female")}</option>
          </select>
        </div>
      </div>

      {/* 체력 수준 */}
      <div>
        <label className="block text-sm text-gray-400 mb-1">
          {t("FightSimulator.fitnessLevel")}
        </label>
        <div className="flex gap-2">
          {(["high", "medium", "low"] as const).map((level) => (
            <button
              key={level}
              onClick={() => onUpdate({ fitnessLevel: level })}
              className={`flex-1 py-2 rounded-lg font-medium transition-colors ${
                fighter.fitnessLevel === level
                  ? classes.button + " text-white"
                  : "bg-gray-700 text-gray-300 hover:bg-gray-600"
              }`}
            >
              {t(`FightSimulator.fitness${level.charAt(0).toUpperCase() + level.slice(1)}`)}
            </button>
          ))}
        </div>
      </div>

      {/* 실전 경험 */}
      <div className="flex items-center justify-between">
        <label className="text-sm text-gray-400">
          {t("FightSimulator.realFightExperience")}
        </label>
        <button
          onClick={() =>
            onUpdate({ hasRealFightExperience: !fighter.hasRealFightExperience })
          }
          className={`w-12 h-6 rounded-full transition-colors relative ${
            fighter.hasRealFightExperience ? classes.button : "bg-gray-600"
          }`}
        >
          <span
            className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
              fighter.hasRealFightExperience ? "left-7" : "left-1"
            }`}
          />
        </button>
      </div>

      {/* 격투 경력 */}
      <div>
        <label className="block text-sm text-gray-400 mb-2">
          {t("FightSimulator.martialArts")}
        </label>

        {/* 기존 경력 목록 */}
        {fighter.martialArts.length > 0 && (
          <div className="space-y-2 mb-3">
            {fighter.martialArts.map((ma) => (
              <div
                key={ma.id}
                className="flex items-center justify-between bg-gray-800 px-3 py-2 rounded-lg"
              >
                <span className="text-white">
                  {ma.type}{" "}
                  <span className="text-gray-400">
                    ({ma.durationMonths}
                    {t("FightSimulator.months")})
                  </span>
                </span>
                <button
                  onClick={() => onRemoveMartialArt(ma.id)}
                  className="text-red-400 hover:text-red-300"
                >
                  <i className="fas fa-times" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* 경력 추가 */}
        <div className="flex gap-2">
          <input
            type="text"
            value={newMartialArtType}
            onChange={(e) => setNewMartialArtType(e.target.value)}
            placeholder={t("FightSimulator.martialArtTypePlaceholder")}
            className="flex-1 px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:border-gray-400 focus:outline-none"
            onKeyPress={(e) => e.key === "Enter" && handleAddMartialArt()}
          />
          <input
            type="number"
            value={newMartialArtDuration}
            onChange={(e) =>
              setNewMartialArtDuration(Math.max(1, parseInt(e.target.value) || 1))
            }
            className="w-16 px-2 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm text-center focus:border-gray-400 focus:outline-none"
          />
          <span className="py-2 text-gray-400 text-sm">
            {t("FightSimulator.months")}
          </span>
          <button
            onClick={handleAddMartialArt}
            disabled={!newMartialArtType.trim()}
            className={`px-3 py-2 rounded-lg text-white transition-colors ${
              newMartialArtType.trim()
                ? classes.button
                : "bg-gray-600 cursor-not-allowed"
            }`}
          >
            <i className="fas fa-plus" />
          </button>
        </div>
      </div>

      {/* 헬스 경력 섹션 */}
      <div>
        <label className="block text-sm text-gray-400 mb-2">
          💪 {t("FightSimulator.healthExperience")}
        </label>
        <button
          onClick={() =>
            onUpdate({ health: { ...fighter.health, hasHealthExperience: !fighter.health.hasHealthExperience } })
          }
          className={`w-full h-6 rounded-full transition-colors relative ${
            fighter.health.hasHealthExperience ? classes.button : "bg-gray-600"
          }`}
        >
          <span
            className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
              fighter.health.hasHealthExperience ? "left-7" : "left-1"
            }`}
          />
        </button>

        {/* 헬스 경험이 있으면 상세 정보 입력 */}
        {fighter.health.hasHealthExperience && (
          <div className="mt-3 space-y-2">
            {/* 헬스 경력 년수 */}
            <div>
              <label className="block text-xs text-gray-400 mb-1">
                {t("FightSimulator.yearsOfTraining")}
              </label>
              <div className="flex">
                <input
                  type="number"
                  value={fighter.health.yearsOfTraining}
                  onChange={(e) =>
                    onUpdate({
                      health: {
                        ...fighter.health,
                        yearsOfTraining: Math.max(0, parseInt(e.target.value) || 0),
                      },
                    })
                  }
                  className="w-full px-2 py-1 bg-gray-800 border border-gray-600 rounded-l-lg text-white focus:border-gray-400 focus:outline-none text-sm"
                />
                <span className="px-2 py-1 bg-gray-700 border border-l-0 border-gray-600 rounded-r-lg text-gray-400 text-sm">
                  {t("FightSimulator.years")}
                </span>
              </div>
            </div>

            {/* 벤치프레스, 데드리프트, 스쿼트 */}
            <div className="grid grid-cols-3 gap-2">
              {["benchPress", "deadlift", "squat"].map((lift) => (
                <div key={lift}>
                  <label className="block text-xs text-gray-400 mb-1">
                    {t(`FightSimulator.${lift}`)}
                  </label>
                  <div className="flex">
                    <input
                      type="number"
                      value={(fighter.health[lift as keyof typeof fighter.health] as number) || 0}
                      onChange={(e) =>
                        onUpdate({
                          health: {
                            ...fighter.health,
                            [lift]: Math.max(0, parseInt(e.target.value) || 0),
                          },
                        })
                      }
                      className="w-full px-2 py-1 bg-gray-800 border border-gray-600 rounded-l-lg text-white focus:border-gray-400 focus:outline-none text-xs"
                    />
                    <span className="px-1 py-1 bg-gray-700 border border-l-0 border-gray-600 rounded-r-lg text-gray-400 text-xs">
                      kg
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* 유산소 운동 섹션 */}
      <div>
        <label className="block text-sm text-gray-400 mb-2">
          🏃 {t("FightSimulator.cardioExperience")}
        </label>
        <button
          onClick={() =>
            onUpdate({
              cardio: { ...fighter.cardio, hasCardioExperience: !fighter.cardio.hasCardioExperience },
            })
          }
          className={`w-full h-6 rounded-full transition-colors relative ${
            fighter.cardio.hasCardioExperience ? classes.button : "bg-gray-600"
          }`}
        >
          <span
            className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
              fighter.cardio.hasCardioExperience ? "left-7" : "left-1"
            }`}
          />
        </button>

        {/* 유산소 경험이 있으면 러닝 거리 입력 */}
        {fighter.cardio.hasCardioExperience && (
          <div className="mt-3">
            <label className="block text-xs text-gray-400 mb-1">
              {t("FightSimulator.maxRunningDistance")}
            </label>
            <div className="flex">
              <input
                type="number"
                value={fighter.cardio.maxRunningDistance}
                onChange={(e) =>
                  onUpdate({
                    cardio: {
                      ...fighter.cardio,
                      maxRunningDistance: Math.max(0, parseFloat(e.target.value) || 0),
                    },
                  })
                }
                step="0.1"
                className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-l-lg text-white focus:border-gray-400 focus:outline-none text-sm"
              />
              <span className="px-3 py-2 bg-gray-700 border border-l-0 border-gray-600 rounded-r-lg text-gray-400">
                km
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FighterForm;
